import { Play } from 'lucide-react';

import { useTutorial } from '@/components/tutorial/use-tutorial';
import { HOST_MB } from '@/lib/constants';
import { useContractsData } from '@/hooks/api/use-contracts';
import useBrandName from '@/hooks/use-brand-name';
import {
  getApporteur1LastContract,
  getApporteurNomLastContract,
  hasContractApporteur43397,
} from '@/lib/contracts';

const formatPhone = (phone?: string) => {
  if (!phone) return phone;
  const digits = phone.replace(/\D/g, '');
  return digits.replace(/(\d{2})(?=\d)/g, '$1 ').trim();
};

export interface WelcomeProps {
  firstName?: string;
  lastName?: string;
  titre?: string;
  userId?: number | string;
}

const Welcome = ({ firstName, lastName, titre, userId }: WelcomeProps) => {
  const brandName = useBrandName();
  const contracts = useContractsData();
  const apporteurNomLastContract = getApporteurNomLastContract(contracts);
  const apporteur = getApporteur1LastContract(contracts);
  const hasApporteur43397 = hasContractApporteur43397(contracts);
  const isAmGestion = HOST_MB !== 43397;
  const displayBrand = HOST_MB !== 43397 && apporteurNomLastContract ? apporteurNomLastContract : brandName;
  const { requestTutorial } = useTutorial() as { requestTutorial?: () => void };
  const displayName = titre === 'SOC' ? lastName : firstName;
  // Greeting based on time of day
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Bonjour';
    if (hour < 18) return 'Bon après-midi';
    return 'Bonsoir';
  };

  
  return (
    <div data-tutorial="welcome" className="relative overflow-hidden rounded-2xl bg-primary p-6 md:p-8 text-primary-foreground mb-6">
      {/* Decorative bubbles */}
      <div className="absolute -top-24 -right-12 w-72 h-72 bg-white/10 rounded-full" />
      <div className="absolute -bottom-32 -left-16 w-64 h-64 bg-white/5 rounded-full" />

      {/* Coordonnées de l'apporteur 1 en haut à droite */}
      {isAmGestion && apporteur && (
        <div className="absolute top-4 right-4 z-20 max-w-[45%] text-right text-[15px] leading-tight text-primary-foreground/90 space-y-0.5">
          <p className="font-semibold uppercase tracking-wide text-primary-foreground/70">Votre conseiller :</p>
          {apporteur.raisonSociale && <p className="font-semibold">{apporteur.raisonSociale}</p>}
          {apporteur.rue1 && <p>{apporteur.rue1}</p>}
          {(apporteur.codePostal || apporteur.ville) && (
            <p>{[apporteur.codePostal, apporteur.ville].filter(Boolean).join(' ')}</p>
          )}
          {apporteur.phone && <p className="font-bold">{formatPhone(apporteur.phone)}</p>}
        </div>
      )}

      {/* Content */}
      <div className="relative z-10">
        <p className="text-sm font-medium mb-4 text-primary-foreground">Assuré n°{userId}</p>

        <h1 className="text-2xl md:text-3xl font-bold mb-2">
          {getGreeting()}, {displayName}&nbsp;!&nbsp;✌️
        </h1>

        <p className="text-primary-foreground text-sm md:text-base max-w-3xl leading-relaxed">
          {hasApporteur43397
            ? `Ravi de vous retrouver sur votre espace assuré ${displayBrand}.`
            : 'Ravi de vous retrouver sur votre espace assuré.'}
        </p>

        <button
          onClick={requestTutorial}
          className="mt-6 inline-flex items-center gap-2 px-6 py-2 bg-white/20 hover:bg-white/30 text-primary-foreground text-sm font-medium rounded-lg transition-colors cursor-pointer"
        >
          <Play className="w-4 h-4" />
          Découvrir mon espace
        </button>
      </div>
    </div>
  );
};

export default Welcome;
